import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Lock } from "lucide-react";

import { DashboardShell } from "@/components/DashboardShell";
import { ListingForm } from "@/components/ListingForm";
import { PARTNER_NAV } from "@/lib/nav";
import { SERVICE_LABEL, SERVICE_TYPES, useMyServices, type ServiceType } from "@/lib/services";
import { fetchListing } from "@/lib/listings";

export const Route = createFileRoute("/_authenticated/partner/listings/$service/$listingId")({
  head: () => ({
    meta: [
      { title: "Edit listing · Landoo" },
      {
        name: "description",
        content: "Edit a draft or rejected Landoo listing and resubmit it for approval.",
      },
    ],
  }),
  component: EditListingPage,
});

function EditListingPage() {
  const { service, listingId } = useParams({
    from: "/_authenticated/partner/listings/$service/$listingId",
  });
  const { canList, isLoading } = useMyServices();
  const svc = SERVICE_TYPES.includes(service as ServiceType) ? (service as ServiceType) : null;

  const { data: row, isLoading: rowLoading } = useQuery({
    queryKey: ["partner", "listing-row", service, listingId],
    queryFn: () => fetchListing(svc!, listingId),
    enabled: !!svc,
  });

  if (!svc) {
    return (
      <DashboardShell title="Edit listing" role="Partner" items={PARTNER_NAV}>
        <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          Unknown listing type.
        </div>
      </DashboardShell>
    );
  }

  if (!isLoading && !canList(svc)) {
    return (
      <DashboardShell
        title={`Edit ${SERVICE_LABEL[svc]} listing`}
        role="Partner"
        items={PARTNER_NAV}
      >
        <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          This service is not activated.
          <div className="mt-3">
            <Link
              to="/partner/services"
              className="inline-flex rounded-full bg-brand px-4 py-2 text-xs font-semibold text-brown-dark"
            >
              Go to my services
            </Link>
          </div>
        </div>
      </DashboardShell>
    );
  }

  const status = (row as { status?: string } | null | undefined)?.status;
  if (!rowLoading && status === "pending") {
    return (
      <DashboardShell
        title={`Edit ${SERVICE_LABEL[svc]} listing`}
        role="Partner"
        items={PARTNER_NAV}
      >
        <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-brown-dark">
            <Lock className="h-3.5 w-3.5" /> Under Admin Review
          </div>
          <p>
            Editing is locked while this listing is under admin review. You can edit again after it
            is approved or if changes are requested.
          </p>
          <div className="mt-3">
            <Link
              to="/partner/listings"
              className="inline-flex rounded-full bg-brand px-4 py-2 text-xs font-semibold text-brown-dark"
            >
              Back to My listings
            </Link>
          </div>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title={`Edit ${SERVICE_LABEL[svc]} listing`} role="Partner" items={PARTNER_NAV}>
      <ListingForm service={svc} listingId={listingId} />
    </DashboardShell>
  );
}
